import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Check, ShoppingBasket, Ship } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { SectionTitle } from "./Sections";

const PACKAGE_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_organic-powder-hub-1/artifacts/3t7mmd6c_foto%20bungkus%20Agrodyke.jpg";

export const ProductsSection = () => {
  const { t } = useTranslation();
  const { products, addItem, setCartOpen } = useCart();
  const features = t("products.features", { returnObjects: true });
  const p = products[0];
  if (!p) return null;

  const cardCls = "relative bg-white rounded-3xl p-8 border-2 shadow-[0_8px_30px_rgba(109,76,65,0.08)] hover:-translate-y-2 transition-transform flex flex-col";

  return (
    <section id="products" className="bg-brand-cream py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionTitle title={t("products.title")} subtitle={t("products.subtitle")} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5 }} data-testid="product-card-local" className={`${cardCls} border-brand-orange`}>
            <span className="absolute -top-3 start-8 px-4 py-1 rounded-full bg-brand-orange text-white text-xs font-bold uppercase tracking-wider">
              {t("products.localTitle")}
            </span>
            <div className="h-56 rounded-2xl bg-brand-yellow/40 flex items-center justify-center overflow-hidden">
              <img src={PACKAGE_IMG} alt="Agrodyke 1 kg" className="h-full w-full object-contain p-2" />
            </div>
            <div className="mt-6 flex-1 flex flex-col">
              <h3 className="font-heading text-3xl text-brand-dark uppercase">Agrodyke 1 kg</h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span data-testid="product-price-local" className="font-heading text-4xl text-brand-orange">
                  Rp {p.price_idr.toLocaleString("id-ID")}
                </span>
                <span className="text-xs text-brand-brown font-semibold">{t("products.perKg")}</span>
              </div>
              <p data-testid="local-min-note" className="mt-2 inline-flex self-start px-3 py-1 rounded-full bg-brand-yellow/60 text-xs font-bold text-brand-dark">
                {t("products.localMin")}
              </p>
              <ul className="mt-4 space-y-2">
                {features.map((f, fi) => (
                  <li key={fi} className="flex items-center gap-2 text-sm text-brand-brown">
                    <Check size={16} className="text-brand-green shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-brand-brown/70 font-semibold italic mt-3">* {t("products.taxNote")}</p>
              <button data-testid="add-to-cart-agrodyke-1kg"
                onClick={() => { addItem(p.product_id, 5); toast.success(t("products.added")); setCartOpen(true); }}
                className="mt-auto pt-5 w-full">
                <span className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 min-h-[52px] rounded-full bg-brand-orange text-white font-bold text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors">
                  <ShoppingBasket size={18} /> {t("products.addToCart")}
                </span>
              </button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }} data-testid="product-card-international" className={`${cardCls} border-brand-green`}>
            <span className="absolute -top-3 start-8 px-4 py-1 rounded-full bg-brand-green text-white text-xs font-bold uppercase tracking-wider">
              {t("products.intlTitle")}
            </span>
            <div className="h-56 rounded-2xl bg-brand-yellow/40 flex items-center justify-center overflow-hidden">
              <img src="/images/sack-25kg.jpg" alt="Agrodyke 25 kg sack" className="h-full w-full object-contain p-2" />
            </div>
            <div className="mt-6 flex-1 flex flex-col">
              <h3 className="font-heading text-3xl text-brand-dark uppercase">Agrodyke Export</h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span data-testid="product-price-international" className="font-heading text-4xl text-brand-green">
                  ${p.price_usd.toFixed(2)}
                </span>
                <span className="text-xs text-brand-brown font-semibold">{t("products.perKg")}</span>
              </div>
              <p data-testid="intl-min-note" className="mt-2 inline-flex self-start px-3 py-1 rounded-full bg-brand-green/10 text-xs font-bold text-brand-dark">
                {t("products.intlMin")}
              </p>
              <ul className="mt-4 space-y-2">
                {features.map((f, fi) => (
                  <li key={fi} className="flex items-center gap-2 text-sm text-brand-brown">
                    <Check size={16} className="text-brand-green shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-brand-green font-bold italic mt-3">* {t("products.intlTaxNote")}</p>
              <a href="#quotation" data-testid="request-quote-international" className="mt-auto pt-5 w-full block">
                <span className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 min-h-[52px] rounded-full bg-brand-green text-white font-bold text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors">
                  <Ship size={18} /> {t("products.requestQuote")}
                </span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
